import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ListIcon from '@mui/icons-material/List';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import { viewTypeIconButtonSx } from 'components/Search/styles';
import { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearch } from 'components/Search/SearchContextProvider';

/**
 * ResultsViewToggle
 * collapses to a single icon menu on mobile
 * expands to the three individual icon buttons on larger screens.
 */
type ResultsViewToggleProps = {
  iconButtonSx?: SystemStyleObject | ((theme: Theme) => SystemStyleObject);
};

export const ResultsViewToggle = ({ iconButtonSx }: ResultsViewToggleProps) => {
  const { view, swapView } = useSearch();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const strCardView = t('blox.search.view.card_view');
  const strSummaryView = t('blox.search.view.summary_view');
  const strListView = t('blox.search.view.list_view');

  const viewOptions = [
    {
      key: 'card' as const,
      label: strCardView,
      Icon: GridViewRoundedIcon,
      testId: 'editorial-search-grid-view',
      ariaLabel: strCardView,
    },
    {
      key: 'summary' as const,
      label: strSummaryView,
      Icon: ViewAgendaIcon,
      testId: 'editorial-search-summary-view',
      ariaLabel: 'summary-view',
    },
    {
      key: 'list' as const,
      label: strListView,
      Icon: ListIcon,
      testId: 'editorial-search-list-view',
      ariaLabel: 'list-view',
    },
  ];

  const activeOption = viewOptions.find((o) => o.key === view) ?? viewOptions[0];
  const ActiveIcon = activeOption.Icon;

  if (isMobile) {
    return (
      <>
        <Tooltip
          title={activeOption.label}
          disableInteractive
        >
          <IconButton
            data-testid="editorial-search-view-menu-trigger"
            aria-label="view type menu"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={(theme) => ({
              ...viewTypeIconButtonSx,
              ...(typeof iconButtonSx === 'function'
                ? iconButtonSx(theme)
                : iconButtonSx),
            })}
          >
            <ActiveIcon color="primary" />
          </IconButton>
        </Tooltip>
        <Menu
          id="editorial-search-view-type-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          slotProps={{
            list: { dense: true },
          }}
        >
          {viewOptions.map(({ key, label, Icon }) => (
            <MenuItem
              key={key}
              selected={view === key}
              onClick={() => {
                swapView(key);
                setAnchorEl(null);
              }}
            >
              <ListItemIcon>
                <Icon
                  fontSize="small"
                  color={view === key ? 'primary' : 'inherit'}
                />
              </ListItemIcon>
              <ListItemText>{label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <>
      {viewOptions.map(({ key, label, Icon, testId, ariaLabel }, idx) => (
        <Fragment key={key}>
          {idx > 0 && (
            <Divider
              key={`${key}-divider`}
              orientation="vertical"
              flexItem
            />
          )}
          <Tooltip
            title={label}
            disableInteractive
            data-testid={`${testId}-tooltip`}
          >
            <IconButton
              data-testid={testId}
              aria-label={ariaLabel}
              onClick={() => swapView(key)}
              sx={viewTypeIconButtonSx}
            >
              <Icon color={view === key ? 'primary' : 'disabled'} />
            </IconButton>
          </Tooltip>
        </Fragment>
      ))}
    </>
  );
};

ResultsViewToggle.displayName = 'ResultsViewToggle';
export default ResultsViewToggle;
